/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { css } from 'styled-components';

import { hexToRGBA } from '../../utils';
import { colors } from '../colors';
import fonts, { fontSizes } from '../fonts';
import spacing from '../spacing';
import marginStyles, { MarginProps } from './marginPropStyles';

type ButtonSize = 'lg' | 'md' | 'sm';

export interface ButtonProps {
  inline?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  quarternary?: boolean;
  // Primary use case is for accessible icon buttons https://a11y-101.com/development/icons-and-links
  $invisible?: boolean;
  size?: ButtonSize;
}

export function getHeight(size: ButtonSize = 'lg') {
  switch (size) {
    case 'lg':
      return '48px';
    case 'md':
      return '40px';
    case 'sm':
      return '32px';
  }
}

function getPadding(size: ButtonSize = 'lg') {
  switch (size) {
    case 'lg':
      return `${spacing.sm} ${spacing.lg}`;
    case 'md':
      return `${spacing.xs} ${spacing.md}`;
    case 'sm':
      return `${spacing.xxs} ${spacing.sm}`;
  }
}

const primaryCss = css`
  background-color: ${colors.brandPurple};
  border: 2px solid transparent;
  color: ${colors.white};
  outline: none;

  &:focus {
    border: 2px solid ${colors.white};
    box-shadow: 0 0 0 2px ${colors.brandPurple};
  }

  &:hover {
    background-color: ${colors.darkPurple};
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &:focus:hover {
    border: 2px solid transparent;
  }
`;

const secondaryCss = css`
  background-color: transparent;
  border: 1px solid ${colors.grey20};
  color: ${colors.brandPurple};
  outline: none;

  &:focus {
    border: 1px solid ${colors.grey30};
    box-shadow: 0 0 0 2px ${colors.brandPurple};
  }

  &:hover {
    background-color: ${colors.white};
    border: 1px solid ${colors.grey30};
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &:focus:hover {
    border: 1px solid ${colors.grey20};
  }
`;

const tertiaryCss = css`
  background-color: ${colors.grey15};
  border: 1px solid ${colors.grey20};
  color: ${colors.brandPurple};
  outline: none;

  &:focus {
    border: 1px solid ${colors.grey30};
    box-shadow: 0 0 0 2px ${colors.brandPurple};
  }

  &:hover {
    border: 1px solid ${colors.grey30};
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &:focus:hover {
    border: 1px solid ${colors.grey20};
  }
`;
const quaternaryCss = css`
  background-color: ${colors.white};
  border: 1px solid ${colors.grey20};
  color: ${colors.brandPurple};
  outline: none;

  &:focus {
    border: 1px solid ${colors.grey30};
    box-shadow: 0 0 0 2px ${colors.brandPurple};
  }

  &:hover {
    border: 1px solid ${colors.grey30};
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &:focus:hover {
    border: 1px solid ${colors.grey20};
  }
`;

const transparentCss = css`
  background-color: transparent;
  border: 0;
  outline: none;
`;

type Props = ButtonProps & MarginProps;

export const buttonCss = css`
  ${(props: Props) =>
    !!props.$invisible
      ? transparentCss
      : !!props.secondary
      ? secondaryCss
      : !!props.tertiary
      ? tertiaryCss
      : !!props.quarternary
      ? quaternaryCss
      : primaryCss}
  ${marginStyles}
  border-radius: 48px;
  cursor: pointer;
  display: ${(props: Props) => (!!props.inline ? 'inline-block' : 'block')};
  font: ${(props: Props) =>
    props.size === 'sm'
      ? fonts.bold(fontSizes.caption)
      : fonts.bold(fontSizes.h6)};
  height: ${(props: Props) =>
    !props.$invisible ? getHeight(props.size) : null};

  /* vertically center text */
  line-height: 12px;
  padding: ${(props: Props) =>
    !props.$invisible ? getPadding(props.size) : 0};
  text-align: center;
  white-space: nowrap;

  &:disabled,
  [aria-disabled='true'],
  [aria-disabled='true'] {
    background-color: ${colors.grey05};
    color: ${hexToRGBA(colors.grey50, 0.5)} !important;
    cursor: not-allowed;
    opacity: 50%;

    & * {
      color: ${hexToRGBA(colors.grey50, 0.5)} !important;
      stroke: ${hexToRGBA(colors.grey50, 0.5)} !important;
    }
  }
`;
